'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useCollapsibleRail } from '@/hooks/useCollapsibleRail'
import { RailCollapseButton, RailReopenHandle } from '@/components/content/rail-collapse'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { PanelRightOpen } from 'lucide-react'

/**
 * The bench split — sticky media pane (left) + reference pane (right).
 * Shared by the component and design configs. Behaviors, matching the scene
 * viewer's spec sidebar:
 *  - the reference pane is a **separated card** (24px gap from the dark title
 *    bar, like the step-photo card);
 *  - on desktop (`lg`+) it sits inline and **collapses** via a chevron
 *    (persisted like the reader rails); collapsed, the media centers and a
 *    right-edge pull-tab reopens it;
 *  - below `lg` it moves into a right-edge **Sheet** opened by a pull-tab, so
 *    the media pane gets the full width and the reference is always reachable
 *    (the desktop collapse state never strands it on mobile).
 */
export function BenchColumns({
  storageKey,
  gridRef,
  media,
  reference,
  initialCollapsed,
}: {
  storageKey: string
  gridRef?: React.Ref<HTMLDivElement>
  /** Render the media pane; receives the collapsed state so it can grow the
   *  photo/video taller (capped at the fold) when the reference pane is hidden. */
  media: (collapsed: boolean) => React.ReactNode
  reference: React.ReactNode
  /** Account preference: start the Details pane collapsed (true) / no preference (null). */
  initialCollapsed?: boolean | null
}) {
  const t = useTranslations()
  const { collapsed, toggle } = useCollapsibleRail(storageKey, {
    autoCollapseBelow: 0,
    initialCollapsed,
  })

  // Park the mobile "Details" pull-tab just beneath the sticky site header,
  // whatever its current height. Rather than assume a banner height, we read
  // the header's own live bottom edge: the announcement / impersonation
  // banners above it push it down by exactly their (variable) heights, and it
  // clamps to the viewport top once you scroll past them. Measuring that edge
  // keeps the tab a couple pixels under the bar in every state — banner shown
  // at any height, banner dismissed, or scrolled.
  const [tabTop, setTabTop] = useState(96)
  useEffect(() => {
    const header = document.querySelector('header')
    if (!header) return
    const GAP = 6 // a couple px of separation below the header bar
    let raf = 0
    const measure = () => {
      raf = 0
      setTabTop(Math.max(0, Math.round(header.getBoundingClientRect().bottom + GAP)))
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    // Re-measure when the header's own height changes (responsive wrap, etc.).
    const ro = new ResizeObserver(schedule)
    ro.observe(header)
    // The announcement / impersonation banners are fetched async and can be
    // dismissed — each mounts or unmounts a sibling of the header and shifts
    // where its bottom lands. A full-height flex column absorbs that without
    // the document resizing, so watch the shared parent for those siblings
    // being added/removed rather than relying on a size change.
    const parent = header.parentElement
    const mo = parent ? new MutationObserver(schedule) : null
    if (parent) mo!.observe(parent, { childList: true })
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      ro.disconnect()
      mo?.disconnect()
    }
  }, [])

  return (
    <>
      <div
        ref={gridRef}
        className={`mx-auto grid max-w-[1280px] items-start ${
          collapsed ? 'lg:grid-cols-1' : 'lg:grid-cols-[54fr_46fr]'
        }`}
      >
        {/* Left — sticky media pane (centered + capped when collapsed). */}
        <div
          className={`p-6 lg:sticky lg:top-0 ${
            collapsed ? 'lg:mx-auto lg:w-full lg:max-w-[1180px]' : ''
          }`}
        >
          {media(collapsed)}
        </div>

        {/* Right — reference pane. Inline on desktop only (mobile uses the
            Sheet below); a separated card with a collapse control. */}
        {!collapsed && (
          <div className="hidden p-6 lg:block lg:pl-0">
            <div className="border bg-white" style={{ borderColor: '#E2E4EC' }}>
              <div className="flex justify-start px-3 pt-3">
                <RailCollapseButton side="right" label={t('rails.details')} onClick={toggle} />
              </div>
              <div className="px-7 pb-8 pt-1">{reference}</div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop reopen tab (only while collapsed). */}
      {collapsed && <RailReopenHandle side="right" label={t('rails.details')} onClick={toggle} />}

      {/* Mobile: the reference lives in a right-edge Sheet, opened by a pull-tab. */}
      <div className="lg:hidden">
        <Sheet>
          <SheetTrigger
            render={
              <button
                type="button"
                aria-label={t('floggerBench.openDetails')}
                aria-haspopup="dialog"
                className="fixed right-0 z-40 flex flex-col items-center gap-1 rounded-l-md px-1.5 py-3 text-white shadow-lg transition-transform hover:brightness-110 lg:hidden"
                style={{ top: tabTop, background: 'var(--rope-deep)' }}
              >
                <PanelRightOpen className="h-4 w-4" aria-hidden />
                <span className="font-mono text-[9px] uppercase tracking-[0.14em] [writing-mode:vertical-rl] [text-orientation:mixed]">
                  {t('rails.details')}
                </span>
              </button>
            }
          />
          {/* Near-full-width so the wide rope-consumption / wrap tables inside
              fit without horizontal scrolling. The width lives under the same
              `data-[side=right]:` variant as the Sheet's own default so
              tailwind-merge replaces it (a plain `w-…` would lose to the
              higher-specificity variant class and leave the drawer at 75%). */}
          <SheetContent
            side="right"
            className="data-[side=right]:w-[95vw] data-[side=right]:sm:max-w-3xl"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{t('rails.details')}</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-3 pb-6 pt-12">{reference}</div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  )
}
